import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { useRecentlyVisited } from '../hooks/useRecentlyVisited';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Users, Wifi, Coffee, Plane, ThumbsUp, Plus, MessageSquare, Shield, Clock, Globe, ArrowLeft, Heart, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { AirportSEO } from '../components/SEOHead';
import ContentGate from '../components/ContentGate';
import AuthModal from '../components/AuthModal';
import CityGuide from '../components/CityGuide';
import ExpediaBanner from '../components/ExpediaBanner';
import NativeAdCard from '../components/NativeAdCard';
import QuizCTABanner from '../components/QuizCTABanner';
import { useAds } from '../hooks/useAds';
// Lazy load the map to avoid SSR issues
const AirportMap = lazy(() => import('../components/AirportMap'));

import { API, WS_BASE } from '../config';
const POST_TYPES = {
  gate_change: { label: 'Gate Change', cls: 'chip-gate' },
  crowd_level: { label: 'Crowd Alert', cls: 'chip-crowd' },
  food_rec: { label: 'Food Tip', cls: 'chip-food' },
  delay_alert: { label: 'Delay Alert', cls: 'chip-delay' },
  lounge_tip: { label: 'Lounge Tip', cls: 'chip-lounge' },
  general: { label: 'Update', cls: 'chip-crowd' },
};

const CROWD_COLORS = {
  low: 'text-[#10B981]',
  medium: 'text-[#FF4500]',
  high: 'text-red-500',
};

export default function AirportPage() {
  const { airportCode } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { add: addVisit } = useRecentlyVisited();
  const [airport, setAirport] = useState(null);
  const [posts, setPosts] = useState([]);
  const [guides, setGuides] = useState([]);
  const [relatedAirports, setRelatedAirports] = useState([]);
  const [mapRoutes, setMapRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postContent, setPostContent] = useState('');
  const [postType, setPostType] = useState('general');
  const [posting, setPosting] = useState(false);
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [guideData, setGuideData] = useState({ title: '', content: '', category: 'tips' });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const wsRef = useRef(null);
  const ads = useAds('airport');

  const code = airportCode?.toUpperCase();

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/airports/${code}`),
      axios.get(`${API}/airports/${code}/feed`),
      axios.get(`${API}/guides/${code}`),
      axios.get(`${API}/airports/${code}/related`),
    ]).then(([airRes, feedRes, guideRes, relRes]) => {
      setAirport(airRes.data);
      setPosts(feedRes.data);
      setGuides(guideRes.data);
      setRelatedAirports(relRes.data || []);
      setLoading(false);
      // Track visit for recently visited
      addVisit({
        type: 'airport',
        href: `/airport/${airRes.data.iata_code}`,
        label: `${airRes.data.iata_code} — ${airRes.data.city}`,
        sublabel: airRes.data.country,
      });
    }).catch(() => setLoading(false));

    // WebSocket for live feed
    const roomId = `airport_${code}`;
    const ws = new WebSocket(`${WS_BASE}/ws/${roomId}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'new_post') {
        setPosts(prev => [data.post, ...prev]);
      }
    };
    return () => ws.close();
  }, [code]);

  const handlePost = async () => {
    if (!user) { toast.error('Please log in to post'); return; }
    if (!postContent.trim()) { toast.error('Write something first'); return; }
    setPosting(true);
    try {
      await axios.post(`${API}/posts`, {
        content: postContent,
        airport_code: code,
        post_type: postType,
        is_anonymous: false,
      }, { withCredentials: true });
      setPostContent('');
      toast.success('Posted!');
    } catch {
      toast.error('Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleGuide = async () => {
    if (!user) { toast.error('Please log in'); return; }
    try {
      await axios.post(`${API}/guides`, { ...guideData, airport_code: code }, { withCredentials: true });
      toast.success('Guide published!');
      setShowGuideDialog(false);
      const r = await axios.get(`${API}/guides/${code}`);
      setGuides(r.data);
    } catch { toast.error('Failed to publish guide'); }
  };

  const handleReaction = async (postId, reaction) => {
    if (!user) { toast.error('Log in to react'); return; }
    try {
      const r = await axios.post(`${API}/posts/${postId}/react`, { post_id: postId, reaction }, { withCredentials: true });
      setPosts(prev => prev.map(p => p.post_id === postId ? r.data : p));
    } catch {}
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <Navbar />
        <main className="flex-1">
          <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
            <Skeleton className="h-48 rounded-2xl" />
            <Skeleton className="h-32 rounded-2xl" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!airport) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <Navbar />
        <main className="flex-1">
          <div className="max-w-5xl mx-auto px-4 py-16 text-center">
            <p className="text-muted-foreground">Airport not found</p>
            <Button className="mt-4" asChild><Link to="/discover">Back to Discover</Link></Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <AirportSEO airport={airport} />
      <Navbar />

      {/* Airport Hero */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ backgroundImage: `url('${airport.image_url}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#111827]/60 to-[#111827]/90" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 text-white">
          {/* Breadcrumb */}
          <Breadcrumbs
            className="text-white/60 mb-4 [&_a]:text-white/60 [&_a:hover]:text-white [&_svg]:text-white/40"
            items={[
              { label: 'Discover', href: '/discover' },
              { label: `${airport.iata_code} — ${airport.city}` },
            ]}
          />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                  <span className="text-[#FF4500]">{airport.iata_code}</span>
                </h1>
                <Badge className={`capitalize px-2 py-0.5 text-xs ${airport.crowd_level === 'low' ? 'bg-[#10B981]/30 text-[#10B981]' : airport.crowd_level === 'high' ? 'bg-red-500/30 text-red-300' : 'bg-[#FF4500]/30 text-[#FF4500]'}`}>
                  {airport.crowd_level} crowd
                </Badge>
              </div>
              <h2 className="text-xl font-semibold">{airport.name}</h2>
              <p className="text-white/70 text-sm mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {airport.city}, {airport.country}
              </p>
            </div>
            <div className="flex gap-3">
              {airport.wifi && <div className="flex items-center gap-1 text-xs text-white/70"><Wifi className="w-3 h-3" />WiFi</div>}
              {airport.lounge && <div className="flex items-center gap-1 text-xs text-white/70"><Coffee className="w-3 h-3" />Lounges</div>}
              {airport.prayer_room && <div className="flex items-center gap-1 text-xs text-white/70"><Shield className="w-3 h-3" />Prayer Room</div>}
            </div>
          </div>
          <p className="text-white/60 text-sm mt-3 max-w-xl">{airport.description}</p>
          <div className="flex gap-4 mt-4 text-sm">
            <span className="text-white/70">{airport.post_count} community posts</span>
            <span className="text-white/70">{airport.terminals?.length} terminals</span>
          </div>
        </div>
      </div>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          {/* 2-col layout: tabs (flex-1) + affiliate banner sidebar (lg only) */}
          <div className="flex gap-6 items-start">

            {/* ── Main content ─────────────────────────────── */}
            <div className="flex-1 min-w-0">
          <Tabs defaultValue="feed">
          <div className="overflow-x-auto -mx-4 px-4 mb-6 scrollbar-hide">
            <TabsList className="w-max flex-nowrap">
              <TabsTrigger value="feed">Live Feed</TabsTrigger>
              <TabsTrigger value="city">City Guide</TabsTrigger>
              <TabsTrigger value="guide">Airport Guide</TabsTrigger>
              <TabsTrigger value="amenities">Amenities</TabsTrigger>
              <TabsTrigger value="map">Map</TabsTrigger>
              <TabsTrigger value="flights">Flights</TabsTrigger>
            </TabsList>
          </div>

          {/* LIVE FEED TAB */}
          <TabsContent value="feed">
            {/* Mobile-only inline ad (hidden on desktop where sidebar shows) */}
            {ads.length > 0 && (
              <div className="lg:hidden mb-4">
                <NativeAdCard ad={ads[0]} variant="route" />
              </div>
            )}
            {/* Mobile-only quiz CTA */}
            <div className="lg:hidden mb-4">
              <QuizCTABanner variant="inline" />
            </div>
            {/* Post composer */}
            {user && (
              <Card className="fly-card p-4 mb-4">
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(POST_TYPES).map(([key, { label, cls }]) => (
                    <button key={key} onClick={() => setPostType(key)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${postType === key ? cls + ' ring-1 ring-[#FF4500]' : 'bg-muted hover:bg-muted/80'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <textarea
                  placeholder={`What's happening at ${airport.city} airport?`}
                  value={postContent}
                  onChange={e => setPostContent(e.target.value)}
                  rows={3}
                  className="w-full p-3 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF4500]/30"
                  data-testid="post-content-input"
                />
                <div className="flex justify-end mt-2">
                  <Button onClick={handlePost} disabled={posting} className="fly-btn-primary" data-testid="submit-post-btn">
                    {posting ? 'Posting...' : 'Post Update'}
                  </Button>
                </div>
              </Card>
            )}

            <div className="space-y-3">
              {posts.length === 0 ? (
                <Card className="fly-card p-8 text-center">
                  <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No updates yet for {code}</p>
                  {!user && <Button size="sm" className="fly-btn-primary mt-3" onClick={() => setShowAuthModal(true)}>Join to Post</Button>}
                </Card>
              ) : (
                <ContentGate
                  user={user}
                  visibleCount={3}
                  title={`Sign in to see all ${posts.length} live updates`}
                  message={`Join Fly N Out free to read all community posts, gate changes, and tips for ${airport.city} Airport.`}
                  onSignUp={() => setShowAuthModal(true)}
                >
                  {posts.map((post, i) => {
                    const ts = POST_TYPES[post.post_type] || POST_TYPES.general;
                    return (
                      <motion.div key={post.post_id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                        <Card className="fly-card p-4" data-testid="feed-post-card">
                          <div className="flex gap-3">
                            <img src={post.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author_id}`} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                <span className="text-sm font-medium">{post.author_name}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${ts.cls}`}>{ts.label}</span>
                                <span className="text-xs text-muted-foreground ml-auto">{timeAgo(post.created_at)}</span>
                              </div>
                              <p className="text-sm leading-relaxed">{post.content}</p>
                              {post.image_url && <img src={post.image_url} alt="" className="mt-2 rounded-xl max-h-60 object-cover w-full" />}
                              <div className="flex items-center gap-3 mt-3">
                                <button onClick={() => handleReaction(post.post_id, 'like')}
                                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#FF4500] transition-colors" data-testid="post-reaction-like-button">
                                  <Heart className="w-3.5 h-3.5" /> {post.reactions?.like || 0}
                                </button>
                                <button onClick={() => handleReaction(post.post_id, 'fire')}
                                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#FF4500] transition-colors">
                                  🔥 {post.reactions?.fire || 0}
                                </button>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </ContentGate>
              )}
            </div>
          </TabsContent>

          {/* CITY GUIDE TAB */}
          <TabsContent value="city">
            <CityGuide destinationCode={code} />
            {/* Routes from this airport */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-base" style={{ fontFamily: 'Space Grotesk' }}>
                  Routes from {code}
                </h3>
                <a href={`/routes?search=${code}`} className="text-xs text-[#FF4500] hover:underline">
                  View all routes →
                </a>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Browse flight communities for routes departing from or arriving at {airport.city}.
              </p>
              <div className="flex flex-wrap gap-2">
                {['/routes'].map((href) => (
                  <a
                    key={href}
                    href={href}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF4500]/10 text-[#FF4500] text-sm font-medium hover:bg-[#FF4500]/20 transition-colors"
                  >
                    <Plane className="w-3.5 h-3.5" /> Browse All Routes
                  </a>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* GUIDE TAB */}
          <TabsContent value="guide">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Community Airport Guide</h3>
              {user && (
                <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="fly-btn-primary"><Plus className="w-3.5 h-3.5 mr-1" />Add Guide</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Airport Guide Entry</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label>Title</Label>
                        <Input value={guideData.title} onChange={e => setGuideData(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Best Food Options" />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={guideData.category} onValueChange={v => setGuideData(p => ({ ...p, category: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['tips', 'lounge', 'food', 'transport', 'wifi', 'shopping'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Content</Label>
                        <Textarea rows={5} value={guideData.content} onChange={e => setGuideData(p => ({ ...p, content: e.target.value }))} placeholder="Share your knowledge..." />
                      </div>
                      <Button className="fly-btn-primary w-full" onClick={handleGuide}>Publish Guide</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            {guides.length === 0 ? (
              <Card className="fly-card p-8 text-center">
                <p className="text-muted-foreground">No guides yet. Be the first to share your knowledge!</p>
              </Card>
            ) : (
              <Accordion type="single" collapsible className="space-y-2">
                {guides.map(guide => (
                  <AccordionItem key={guide.guide_id} value={guide.guide_id} className="fly-card border-0 px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Badge className="text-[10px] bg-[#1E3A8A]/15 text-[#1E3A8A] border-[#1E3A8A]/20">{guide.category}</Badge>
                        <span className="text-sm font-medium">{guide.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4 whitespace-pre-line">
                      {guide.content}
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={async () => { await axios.post(`${API}/guides/${guide.guide_id}/helpful`, {}, { withCredentials: true }); const r = await axios.get(`${API}/guides/${code}`); setGuides(r.data); }} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#10B981] transition-colors">
                          <ThumbsUp className="w-3.5 h-3.5" /> Helpful ({guide.helpful_count})
                        </button>
                        <span className="text-xs text-muted-foreground">by {guide.author_name}</span>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </TabsContent>

          {/* AMENITIES TAB */}
          <TabsContent value="amenities">
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="fly-card p-4">
                <h4 className="font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Facilities</h4>
                <div className="space-y-2">
                  {airport.amenities?.map(a => (
                    <div key={a} className="flex items-center gap-2 text-sm">
                      <span className="w-2 h-2 rounded-full bg-[#10B981]" />
                      {a}
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="fly-card p-4">
                <h4 className="font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Airlines Served</h4>
                <div className="space-y-2">
                  {airport.airlines?.map(a => (
                    <div key={a} className="flex items-center gap-2 text-sm">
                      <Plane className="w-3.5 h-3.5 text-[#FF4500]" /> {a}
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="fly-card p-4">
                <h4 className="font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Terminals</h4>
                <div className="flex flex-wrap gap-2">
                  {airport.terminals?.map(t => (
                    <Badge key={t} className="bg-[#1E3A8A]/15 text-[#1E3A8A] border-[#1E3A8A]/20">{t}</Badge>
                  ))}
                </div>
              </Card>
              <Card className="fly-card p-4">
                <h4 className="font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Quick Info</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Globe className="w-3.5 h-3.5" />{airport.country}</div>
                  <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" />{airport.timezone}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{airport.lat?.toFixed(4)}, {airport.lng?.toFixed(4)}</div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* MAP TAB */}
          <TabsContent value="map">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Airport Location & Routes</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Click any arc or dot to navigate to its community page</p>
                </div>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${airport.lat}&mlon=${airport.lng}&zoom=14`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#FF4500] hover:underline"
                >
                  Open in full map
                </a>
              </div>
              <Suspense fallback={<Skeleton className="h-80 rounded-2xl" />}>
                <AirportMap
                  airport={airport}
                  height="420px"
                  onNavigate={navigate}
                  onRoutesLoaded={setMapRoutes}
                />
              </Suspense>

              {/* Quick info cards */}
              <div className="grid sm:grid-cols-3 gap-3">
                <Card className="fly-card p-3">
                  <p className="text-xs text-muted-foreground">IATA Code</p>
                  <p className="text-lg font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{airport.iata_code}</p>
                </Card>
                <Card className="fly-card p-3">
                  <p className="text-xs text-muted-foreground">ICAO Code</p>
                  <p className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{airport.icao_code}</p>
                </Card>
                <Card className="fly-card p-3">
                  <p className="text-xs text-muted-foreground">Coordinates</p>
                  <p className="text-sm font-medium">{airport.lat?.toFixed(4)}°, {airport.lng?.toFixed(4)}°</p>
                </Card>
              </div>

              {/* Top routes from this airport */}
              {mapRoutes.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                      <Plane className="w-4 h-4 text-[#FF4500]" />
                      Top Routes from {code}
                    </h4>
                    <Link to={`/routes?search=${code}`} className="text-xs text-[#FF4500] hover:underline">
                      View all →
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {mapRoutes.slice(0, 9).map(route => {
                      const orig = route.origin_airport;
                      const dest = route.destination_airport;
                      if (!orig || !dest) return null;
                      const routeId = route.route_id || `${orig.iata_code}-${dest.iata_code}`;
                      return (
                        <Link
                          key={routeId}
                          to={`/route/${routeId}`}
                          className="group flex flex-col p-2.5 rounded-xl border border-border bg-card hover:border-[#FF4500]/40 hover:bg-[#FF4500]/5 transition-all duration-150"
                        >
                          <div className="flex items-center gap-1 mb-1">
                            <span className="text-xs font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>
                              {orig.iata_code}
                            </span>
                            <Plane className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>
                              {dest.iata_code}
                            </span>
                          </div>
                          <span className="text-[10px] text-muted-foreground truncate leading-tight">
                            {dest.city || orig.city}
                          </span>
                          <span className="text-[10px] text-muted-foreground mt-0.5">
                            {route.checkin_count || 0} travelers
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* FLIGHTS TAB */}
          <TabsContent value="flights">
            <FlightsTab airportCode={code} />
          </TabsContent>
        </Tabs>
            </div>{/* end main content */}

            {/* ── Ad — desktop sticky sidebar, fallback to placeholder ──── */}
            <aside className="hidden lg:block flex-shrink-0 w-[300px]">
              <div className="sticky top-20 space-y-4">
                {/* 1. Ad */}
                {ads.length > 0
                  ? <NativeAdCard ad={ads[0]} variant="sidebar" />
                  : <ExpediaBanner />
                }

                {/* 2. Quiz CTA */}
                <QuizCTABanner variant="sidebar" />

                {/* 3. Suggested Airports (same country) */}
                {relatedAirports.length > 0 && (
                  <Card className="fly-card p-4">
                    <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                      <MapPin className="w-4 h-4 text-[#FF4500]" /> More in {airport.country}
                    </h3>
                    <div className="space-y-1.5">
                      {relatedAirports.slice(0, 5).map(ap => (
                        <Link key={ap.iata_code} to={`/airport/${ap.iata_code}`} className="group flex items-center gap-3 p-2 rounded-xl hover:bg-muted/60 transition-colors">
                          {ap.image_url ? (
                            <img src={ap.image_url} alt="" className="w-10 h-8 rounded-lg object-cover flex-shrink-0" loading="lazy" onError={e => e.target.style.display='none'} />
                          ) : (
                            <div className="w-10 h-8 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm">{ap.flag || '✈️'}</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{ap.iata_code}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{ap.city}</p>
                          </div>
                          <ArrowRight className="w-3 h-3 text-muted-foreground group-hover:text-[#FF4500] transition-colors flex-shrink-0" />
                        </Link>
                      ))}
                    </div>
                    <Link to="/discover" className="mt-3 text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                      Browse all airports →
                    </Link>
                  </Card>
                )}
              </div>
            </aside>

          </div>{/* end 2-col */}
        </div>
      </main>
      <Footer />
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  );
}

function FlightsTab({ airportCode }) {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = require('react-router-dom').useNavigate();

  useEffect(() => {
    axios.get(`${API}/flights?airport_code=${airportCode}&limit=20`)
      .then(r => { setFlights(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [airportCode]);

  if (loading) return <Skeleton className="h-40 rounded-2xl" />;

  return (
    <div className="space-y-3">
      {flights.length === 0 ? (
        <Card className="fly-card p-8 text-center"><p className="text-muted-foreground">No flights data for {airportCode}</p></Card>
      ) : flights.map(f => (
        <Card key={f.flight_id} className="fly-card p-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/flight/flight_${f.flight_number}`)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-base font-bold" style={{ fontFamily: 'Space Grotesk' }}>{f.flight_number}</div>
              <div className="text-sm text-muted-foreground">{f.origin_code} <Plane className="w-3 h-3 inline" /> {f.destination_code}</div>
            </div>
            <div className="flex items-center gap-2">
              {f.delay_minutes > 0 && <Badge className="text-[10px] status-delayed">+{f.delay_minutes}m</Badge>}
              <Badge className={`text-[10px] ${f.status === 'on_time' ? 'status-on-time' : f.status === 'boarding' ? 'status-boarding' : 'status-delayed'}`}>{f.status?.replace('_', ' ')}</Badge>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
